import { type AxiosInstance, HttpStatusCode, isAxiosError } from "axios";
import { type SearchCondition, type DataGridRow } from "../types";
import { PictureTypeValues } from "../types/enums";
import { SendToTrackingService } from "@/api/handlers/errors";
import { LakersBackendApiRequestTimeoutError } from "@/utils/errors";

const TIMEOUT_MILLISECOND = 60_000;

export const getStoredPicturesListAPI = async (
  apiClient: AxiosInstance,
  searchCondition: SearchCondition
): Promise<DataGridRow[]> => {
  // 事項種別の項目を作成。
  const pictureTypes: string[] = [];
  if (searchCondition.pictureTypeShoyushajiko)
    pictureTypes.push(PictureTypeValues.SHOYUSHAJIKO);
  if (searchCondition.pictureTypeZenbujiko)
    pictureTypes.push(PictureTypeValues.ZENBUJIKO);

  const params = new URLSearchParams();

  searchCondition.prefectures.forEach((prefecture) => {
    params.append("prefectures", prefecture);
  });

  if (searchCondition.location) {
    params.set("location", searchCondition.location);
  }

  pictureTypes.forEach((pictureType) => {
    params.append("picture_types", pictureType);
  });

  params.set("is_empty_house", searchCondition.isEmptyHouse ? "1" : "0");

  if (searchCondition.ownerName?.trim()) {
    params.set("owner_name", searchCondition.ownerName);
    if (searchCondition.ownerNameSelectType) {
      params.set("owner_name_select_type", searchCondition.ownerNameSelectType);
    }
  }

  if (searchCondition.ownerAddress) {
    params.set("owner_address", searchCondition.ownerAddress);
  }

  if (searchCondition.mochibunFrom) {
    params.set("mochibun_from", searchCondition.mochibunFrom);
  }
  if (searchCondition.mochibunTo) {
    params.set("mochibun_to", searchCondition.mochibunTo);
  }

  if (searchCondition.ownerPropertyCountFrom) {
    params.set(
      "owner_property_count_from",
      searchCondition.ownerPropertyCountFrom
    );
  }
  if (searchCondition.ownerPropertyCountTo) {
    params.set("owner_property_count_to", searchCondition.ownerPropertyCountTo);
  }

  params.set(
    "is_current_owner_only",
    searchCondition.isCurrentOwnerOnly ? "1" : "0"
  );

  params.set(
    "latest_owner_only",
    searchCondition.isLatestOwnerOnly ? "1" : "0"
  );

  // タイムアウトの実装
  // タイムアウトした場合、AbortController を使ってリクエスト自体をキャンセルする
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, TIMEOUT_MILLISECOND);

  try {
    const response = await apiClient.get<DataGridRow[]>(
      "/picture-request/stored-pictures?" + params.toString(),
      { signal: controller.signal }
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.code === "ERR_CANCELED") {
      throw new LakersBackendApiRequestTimeoutError(
        "該当するデータが多すぎて処理できませんでした。より詳細な条件で再度検索してください。"
      );
    }

    if (
      isAxiosError(error) &&
      (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
    ) {
      SendToTrackingService(error);
    }

    // その他のエラー時は空配列を返して検索結果なしと扱う
    return [];
  } finally {
    clearTimeout(timeoutId);
  }
};
